import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, type Observable } from 'rxjs';

import { type ApiCallResponse, ApisApiService } from '@blaze-shared/connectors-data-access';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  convertApiCallSchemaToWorkflowSchema,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { type ApiCallWorkflowNode } from '@blaze-shared/workflow-domain';

import { NodeResultMessagesService } from '../../node-result-messages.service';
import { mergeData } from '../../row-merge';

/**
 * Calls a connector's action and merges what comes back into the node's input.
 *
 * How many calls go out follows the input's shape: a list of rows makes one call per row and
 * merges each response into its own row, whereas a single object makes one call whose response
 * merges into that object — and a response that is itself a list fans out into a row each.
 */
@Injectable({ providedIn: 'root' })
export class ApiCallActionService {
  private readonly apisApiService = inject(ApisApiService);
  private readonly iss = inject(IntelliSenseService);
  private readonly nodeResultMessages = inject(NodeResultMessagesService);
  private readonly notificationService = inject(NotificationService);
  private readonly schemaService = inject(SchemaService);

  apicall(
    node: ApiCallWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
    nodeInputSchema: WorkflowSchema,
    projectId: string,
    workflowId: string,
  ): Observable<any> {
    const apiCallSchema = this.schemaService.getApiCallSchema(node);
    const responseSchema = apiCallSchema
      ? convertApiCallSchemaToWorkflowSchema(apiCallSchema, workflowId)
      : undefined;

    const apiCall = Array.isArray(nodeInput)
      ? this.apicallWithArray(node, nodeInput, ctx, nodeInputSchema, responseSchema, projectId)
      : this.apicallWithObject(node, nodeInput, ctx, nodeInputSchema, responseSchema, projectId);

    const { errorMessage, successMessage } = this.nodeResultMessages.getErrorAndSuccessMessage(
      node,
      nodeInput,
      ctx,
    );
    return apiCall.pipe(
      map((response) => {
        if (successMessage) {
          this.notificationService.success(successMessage, true);
        }
        return Array.isArray(response) ? response : [response];
      }),
      catchError((error) => {
        throw errorMessage ? new Error(errorMessage) : error;
      }),
    );
  }

  private apicallWithArray(
    node: ApiCallWorkflowNode,
    nodeInput: any[] = [],
    ctx: RuntimeContext,
    nodeInputSchema: WorkflowSchema,
    responseSchema: WorkflowSchema | undefined,
    projectId: string,
  ): Observable<any> {
    const assignments = node.assignments ?? [];
    const apiRequests: Observable<ApiCallResponse>[] = nodeInput.map((row) => {
      const context = this.iss.updateRuntimeContext(ctx, row);
      let evaluatedParams: { [key: string]: any } = {};
      if (assignments?.length) {
        assignments.forEach((assignment) => {
          const columnName = this.schemaService.getColumnName(assignment.name) || '';
          evaluatedParams[columnName] = this.iss.eval(assignment.value, context);
        });
      } else {
        evaluatedParams = this.iss.eval(node.parameters, context);
      }
      return this.apisApiService.postExecute(
        node.connectorId,
        node.actionName,
        evaluatedParams,
        projectId,
      );
    });

    return forkJoin(apiRequests).pipe(
      map((responses) =>
        responses.flatMap((apicallResponse, responseIndex) =>
          this.mergeResponseIntoRow(
            apicallResponse.response,
            nodeInput[responseIndex],
            nodeInputSchema,
            responseSchema,
            node.actionName,
          ),
        ),
      ),
    );
  }

  private apicallWithObject(
    node: ApiCallWorkflowNode,
    nodeInput: any = {},
    ctx: RuntimeContext,
    nodeInputSchema: WorkflowSchema,
    responseSchema: WorkflowSchema | undefined,
    projectId: string,
  ): Observable<any> {
    const context = this.iss.updateRuntimeContext(ctx, nodeInput);
    const evaluatedParams = this.iss.eval(node.parameters, context);
    return this.apisApiService
      .postExecute(node.connectorId, node.actionName, evaluatedParams, projectId)
      .pipe(
        map((apicallResponse) => {
          const result = apicallResponse.response;
          if (typeof nodeInput !== 'object') {
            return result;
          }
          const merged = this.mergeResponseIntoRow(
            result,
            nodeInput,
            nodeInputSchema,
            responseSchema,
            node.actionName,
          );
          return Array.isArray(result) ? merged : merged[0];
        }),
      );
  }

  /** Merges one response into one input row, fanning out when the response is a list. */
  private mergeResponseIntoRow(
    response: any,
    row: any,
    nodeInputSchema: WorkflowSchema,
    responseSchema: WorkflowSchema | undefined,
    actionName: string,
  ): any[] {
    const results = Array.isArray(response) ? response : [response];
    return results.map((result) =>
      mergeData(row, result, nodeInputSchema, responseSchema, actionName),
    );
  }
}
